import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import { Select } from "@majitap/ui/arc/select/select";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { partnerOptionsQuery } from "../../partners";
import { useDebounced } from "../../search";
import styles from "./fleet.module.css";
import type { FleetSearch } from "./page";

type SetSearch = (patch: Partial<FleetSearch>, replace?: boolean) => void;

/**
 * The search box of a Fleet view: what's typed goes to the URL's `q` once typing pauses.
 * `clear` empties both, for "Clear filters".
 */
export function useSearchText(q: string | undefined, set: SetSearch) {
  const [typed, setTyped] = useState(q ?? "");
  const settled = useDebounced(typed.trim());
  useEffect(() => {
    if ((q ?? "") !== settled) set({ q: settled || undefined }, true);
  }, [settled, q, set]);
  return {
    typed,
    setTyped,
    clear: () => {
      setTyped("");
      set({ q: undefined });
    },
  };
}

export function SearchBox({
  label,
  placeholder,
  text,
}: {
  label: string;
  placeholder: string;
  text: ReturnType<typeof useSearchText>;
}) {
  return (
    <div className={styles.searchBox}>
      <SearchField label={label} value={text.typed} onValueChange={text.setTyped} placeholder={placeholder} />
    </div>
  );
}

const ANY = "any";

/**
 * Whose: any, the company's own, any partner's, or one partner (listed for staff who can see
 * partners). In the URL as `owner`: "own", "partners" or the partner's id.
 */
export function OwnerFilter({
  value,
  onChange,
  partners: canListPartners,
}: {
  value: string | undefined;
  onChange: (owner: string | undefined) => void;
  partners: boolean;
}) {
  const partners = useQuery({ ...partnerOptionsQuery, enabled: canListPartners });
  return (
    <Select
      label="Owner"
      value={value ?? ANY}
      onValueChange={(v) => onChange(v === ANY ? undefined : v)}
      options={[
        { value: ANY, label: "Any owner" },
        { value: "own", label: "Own fleet" },
        { value: "partners", label: "Partners'" },
        ...(partners.data ?? []),
      ]}
    />
  );
}
